import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { GraduationCap, Hospital, Utensils, Trees, ShoppingBag, Bus, Loader2, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  radiusMeters?: number;
}

type CatKey = 'school' | 'hospital' | 'restaurant' | 'park' | 'shop' | 'transport';

const CATEGORIES: { key: CatKey; label: string; icon: any; query: string }[] = [
  { key: 'school',     label: 'Schools',     icon: GraduationCap, query: 'node["amenity"~"school|university|college"]' },
  { key: 'hospital',   label: 'Hospitals',   icon: Hospital,      query: 'node["amenity"~"hospital|clinic|pharmacy"]' },
  { key: 'restaurant', label: 'Restaurants', icon: Utensils,      query: 'node["amenity"~"restaurant|cafe|fast_food"]' },
  { key: 'park',       label: 'Parks',       icon: Trees,         query: 'node["leisure"~"park|garden"]' },
  { key: 'shop',       label: 'Shopping',    icon: ShoppingBag,   query: 'node["shop"]' },
  { key: 'transport',  label: 'Transport',   icon: Bus,           query: 'node["highway"="bus_stop"]' },
];

function distMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
}

export function NearbyAmenities({ latitude, longitude, radiusMeters = 1500 }: Props) {
  const [active, setActive] = useState<CatKey>('school');
  const hasCoords = latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude);

  const cat = CATEGORIES.find(c => c.key === active)!;

  const { data, isLoading, error } = useQuery({
    queryKey: ['amenities', latitude, longitude, radiusMeters, active],
    enabled: hasCoords,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const q = `[out:json][timeout:15];(${cat.query}(around:${radiusMeters},${latitude},${longitude}););out body 25;`;
      const res = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: 'data=' + encodeURIComponent(q),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (!res.ok) throw new Error('Failed to load amenities');
      const json = await res.json();
      const items = (json.elements || [])
        .map((el: any) => ({
          id: el.id,
          name: el.tags?.name || el.tags?.brand || '(unnamed)',
          lat: el.lat,
          lon: el.lon,
          dist: distMeters(latitude as number, longitude as number, el.lat, el.lon),
        }))
        .filter((x: any) => x.name && x.name !== '(unnamed)')
        .sort((a: any, b: any) => a.dist - b.dist)
        .slice(0, 12);
      return items;
    },
  });

  if (!hasCoords) return null;

  return (
    <section>
      <h2 className="font-display text-lg md:text-xl font-bold mb-3">Nearby Amenities</h2>

      <div className="flex flex-wrap gap-2 mb-3">
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          const isActive = c.key === active;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                isActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {c.label}
            </button>
          );
        })}
      </div>

      <Card className="rounded-xl">
        <CardContent className="p-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading nearby {cat.label.toLowerCase()}…
            </div>
          ) : error ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Could not load amenities right now.</p>
          ) : !data || data.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No {cat.label.toLowerCase()} found within {(radiusMeters / 1000).toFixed(1)} km.</p>
          ) : (
            <ul className="divide-y">
              {data.map((item: any) => (
                <li key={item.id} className="flex items-center justify-between py-2 gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
                    <a
                      href={`https://www.openstreetmap.org/?mlat=${item.lat}&mlon=${item.lon}#map=18/${item.lat}/${item.lon}`}
                      target="_blank" rel="noreferrer"
                      className="text-sm truncate hover:text-primary"
                    >
                      {item.name}
                    </a>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {item.dist < 1000 ? `${item.dist} m` : `${(item.dist / 1000).toFixed(1)} km`}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-muted-foreground mt-2 text-center">
            Data &copy; OpenStreetMap contributors via Overpass API
          </p>
        </CardContent>
      </Card>
    </section>
  );
}
